import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, alpha } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { protectedResources } from '../authConfig';
import DashboardCard from '../components/StatisticsDashboard/DashboardCard';
import MonthRangeFilter from '../components/StatisticsDashboard/MonthRangeFilter';
import TrendAreaChart from '../components/StatisticsDashboard/TrendAreaChart';
import StatusBreakdownChart from '../components/StatisticsDashboard/StatusBreakdownChart';
import CategoryPieChart from '../components/StatisticsDashboard/CategoryPieChart';
import CategoryProgressList from '../components/StatisticsDashboard/CategoryProgressList';
import { getDefaultMonthRange, monthInputToRange, CATEGORICAL_COLORS, STATUS_COLORS } from '../components/StatisticsDashboard/chartUtils';
import NotFoundPage from './ErrorPage';

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || '').trim();

// Entry to /CvMatchingStatistics only requires the "ManagersDashboardPage" role
// permission (checked by ProtectedRoute). Whether this specific sub-dashboard
// is enabled is a separate, narrower grant living in managerDashboardPermissionModel
// — so it's checked here, locally, against /api/managerDashboard/myAccess.
const CV_MATCHING_DASHBOARD_KEY = 'cvMatchingStats';

const DASHBOARD_THEME = {
    trend: { color: CATEGORICAL_COLORS[0], icon: TrendingUpIcon },
    score: { color: STATUS_COLORS.good, icon: EmojiEventsIcon },
    departments: { color: CATEGORICAL_COLORS[3], icon: BusinessIcon },
    jobs: { color: CATEGORICAL_COLORS[1], icon: WorkIcon },
};

const SCORE_GROUPS = [
    { key: 'excellent', label: 'מצוינים (80+)', color: STATUS_COLORS.good, icon: StarIcon },
    { key: 'good', label: 'טובים (60-79)', color: STATUS_COLORS.warning, icon: CheckCircleIcon },
    { key: 'low', label: 'נמוכים (מתחת ל-60)', color: STATUS_COLORS.critical, icon: HourglassEmptyIcon },
];

const { fromMonthInput: DEFAULT_FROM, toMonthInput: DEFAULT_TO } = getDefaultMonthRange();

const CvMatchingStatisticsPage = () => {
    // "checking" | "granted" | "denied"
    const [dashboardAccess, setDashboardAccess] = useState('checking');

    const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
    const [toMonth, setToMonth] = useState(DEFAULT_TO);

    const [monthlyStats, setMonthlyStats] = useState([]);
    const [scoreStats, setScoreStats] = useState([]);
    const [jobStats, setJobStats] = useState([]);
    const [departmentStats, setDepartmentStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });

    useEffect(() => {
        let cancelled = false;

        // A definitive 403 means the backend evaluated access and said no.
        // Anything else (network hiccup, cold-start 5xx, token refresh blip) is a
        // failed check, not a denial — retry once before giving up.
        const checkAccess = async (isRetry = false) => {
            try {
                const result = await fetchRead('GET', `${BASE_URL}/api/managerDashboard/myAccess`);
                if (cancelled) return;
                const allowed = !!result?.isAdmin || !!result?.allowedDashboards?.includes(CV_MATCHING_DASHBOARD_KEY);
                setDashboardAccess(allowed ? 'granted' : 'denied');
            } catch (err) {
                if (cancelled) return;
                if (err?.status === 403 || isRetry) {
                    setDashboardAccess('denied');
                    return;
                }
                setTimeout(() => checkAccess(true), 500);
            }
        };

        checkAccess();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStats = useCallback(async () => {
        if (!fromMonth || !toMonth) return;
        setLoading(true);
        setError(null);
        try {
            const { startDate, endDate } = monthInputToRange(fromMonth, toMonth);
            const params = new URLSearchParams({ startDate, endDate }).toString();
            const [monthlyRes, scoreRes, jobRes, departmentRes] = await Promise.all([
                fetchRead('GET', `${BASE_URL}/api/cvMatching/stats/monthly-stats?${params}`),
                fetchRead('GET', `${BASE_URL}/api/cvMatching/stats/score-breakdown?${params}`),
                fetchRead('GET', `${BASE_URL}/api/cvMatching/stats/job-breakdown?${params}`),
                fetchRead('GET', `${BASE_URL}/api/cvMatching/stats/department-breakdown?${params}`),
            ]);
            setMonthlyStats(monthlyRes.monthlyStats || []);
            setScoreStats(scoreRes.scoreStats || []);
            setJobStats(jobRes.jobStats || []);
            setDepartmentStats(departmentRes.departmentStats || []);
        } catch (err) {
            console.error('Error loading CV-matching statistics:', err);
            setError(err.message || 'שגיאה בטעינת הסטטיסטיקות');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromMonth, toMonth]);

    useEffect(() => {
        if (dashboardAccess === 'granted') loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardAccess, fromMonth, toMonth]);

    const pageColor = CATEGORICAL_COLORS[0];

    if (dashboardAccess === 'checking') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress sx={{ color: pageColor }} />
            </Box>
        );
    }

    if (dashboardAccess === 'denied') {
        return <NotFoundPage />;
    }

    return (
        <Box display="flex" flexDirection="column" gap={3} p={2}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: alpha(pageColor, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <BarChartIcon sx={{ color: pageColor, fontSize: 26 }} />
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight={700}>סטטיסטיקות סריקת קורות חיים</Typography>
                    <Typography variant="body2" color="text.secondary">
                        תמונת מצב לפי טווח התאריכים שנבחר — משתנה בכל הדשבורדים למטה
                    </Typography>
                </Box>
            </Box>

            <MonthRangeFilter
                fromMonth={fromMonth}
                toMonth={toMonth}
                onFromMonthChange={setFromMonth}
                onToMonthChange={setToMonth}
                color={pageColor}
            />

            {error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} p={6}>
                    <CircularProgress sx={{ color: pageColor }} />
                    <Typography variant="body2" color="text.secondary">טוען נתונים...</Typography>
                </Box>
            ) : (
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="מגמת קורות חיים חודשית"
                            subtitle="כמות מועמדים שהועלו"
                            icon={DASHBOARD_THEME.trend.icon}
                            color={DASHBOARD_THEME.trend.color}
                        >
                            {(expanded) => (
                                <TrendAreaChart
                                    monthlyData={monthlyStats}
                                    expanded={expanded}
                                    color={DASHBOARD_THEME.trend.color}
                                    totalLabel="סה״כ מועמדים בטווח שנבחר"
                                    unitLabel="מועמדים"
                                    gradientId="cvMatchingTrendGradient"
                                />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="פילוח לפי ציון התאמה"
                            subtitle="מגמה חודשית — מצוינים / טובים / נמוכים"
                            icon={DASHBOARD_THEME.score.icon}
                            color={DASHBOARD_THEME.score.color}
                        >
                            {(expanded) => (
                                <StatusBreakdownChart
                                    monthlyData={scoreStats}
                                    expanded={expanded}
                                    statusGroups={SCORE_GROUPS}
                                    otherLabel="טרם נותחו"
                                    totalLabel="סה״כ מועמדים"
                                />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="פילוח לפי מחלקה"
                            subtitle="כמות מועמדים לכל מחלקה בטווח שנבחר"
                            icon={DASHBOARD_THEME.departments.icon}
                            color={DASHBOARD_THEME.departments.color}
                        >
                            {(expanded) => (
                                <CategoryPieChart data={departmentStats} expanded={expanded} unitLabel="מועמדים" fallbackName="ללא מחלקה" />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="משרות מובילות"
                            subtitle="10 המשרות המובילות לפי כמות מועמדים"
                            icon={DASHBOARD_THEME.jobs.icon}
                            color={DASHBOARD_THEME.jobs.color}
                        >
                            {() => (
                                <CategoryProgressList data={jobStats} unitLabel="מועמדים" topN={10} />
                            )}
                        </DashboardCard>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default CvMatchingStatisticsPage;
